/**
 * Движок карточной игры «Сека» (36 карт, по 3 карты, торги, игра втемную).
 * Единый источник правил для локальных режимов и основа для онлайна.
 *
 * Иерархия комбинаций (как в распространённых правилах СНГ):
 * 1) Трынка — три одинакового ранга; старшинство трынок: 666 > 777 > … > AAA
 * 2) Трояк — три карты одной масти (сумма очков по масти)
 * 3) Хлюст — две карты одной масти (сумма пары по масти)
 * 4) Старшая карта — если нет комбинации по масти, берётся максимальный номинал
 *
 * Очки карт: 6–9 по номиналу, 10/J/Q/K = 10, туз = 11.
 */

export const DEFAULT_SEKA_CONFIG = {
  /** Взнос каждого в банк до торгов (не дублируется как «ставка к уравниванию») */
  ante: 10,
  /** Минимальный шаг повышения относительно текущей верхней ставки */
  minRaise: 10,
  /** Стартовый стек игрока */
  startingChips: 1000,
  /** После стольких завершённых кругов торгов без вскрытия — авто-вскрытие (0 = выкл.) */
  maxTradingRounds: 3,
  /** Минимальный номер круга (1 = первый круг после раздачи), с которого доступно «Вскрыть» */
  showdownMinRound: 2,
  /** Макс. множитель анте при цепочке «пустых» кругов (свара): 1→2→4→… до потолка */
  svarkaAnteMaxMultiplier: 8,
  /** С этого множителя следующей руки — UI «жёсткой свара» (согласие перед анте). */
  svarkaOptInFromMultiplier: 8,
  /** Сколько подряд «пустых» свар уже произошло до принудительного вскрытия при следующем пустом круге (×1→×2→×4, на 4-й пустой круг — шоудаун). */
  svarkaMaxChainBeforeShowdown: 3,
};
const SEKA_RAKE_PERCENT = 0.02;

/** Старшинство трынок: чем больше число, тем сильнее трынка (666 сильнее тузовой). */
const TRIPLE_RANK_POWER = {
  '6': 80,
  '7': 70,
  '8': 60,
  '9': 50,
  T: 40,
  J: 30,
  Q: 20,
  K: 10,
  A: 0,
};

const RANK_VALUES = {
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  T: 10,
  J: 10,
  Q: 10,
  K: 10,
  A: 11,
};

const RANK_NAMES_GEN = {
  '6': 'шестёрок',
  '7': 'семерок',
  '8': 'восьмёрок',
  '9': 'девяток',
  T: 'десяток',
  J: 'валетов',
  Q: 'дам',
  K: 'королей',
  A: 'тузов',
};

const SUIT_NAMES = {
  H: 'черви',
  D: 'бубны',
  C: 'трефы',
  S: 'пики',
};

export function getCardValue(rank) {
  return RANK_VALUES[rank] ?? 6;
}

/**
 * Оценка руки из трёх строк вида "AH", "TD".
 * @returns {{ category: string, strength: number, value: number, description: string, legacyType: string }}
 */
export function evaluateSekaHand(cards) {
  if (!cards || cards.length !== 3) {
    return {
      category: 'SINGLE',
      strength: 0,
      value: 0,
      type: '—',
      pointValue: 0,
      description: 'Нет карт',
      legacyType: 'SINGLE',
    };
  }

  const ranks = cards.map((c) => c[0]);

  // --- Трынка (три одинакового ранга) ---
  if (ranks[0] === ranks[1] && ranks[1] === ranks[2]) {
    const r = ranks[0];
    const sum = getCardValue(r) * 3;
    const tr = TRIPLE_RANK_POWER[r] ?? 0;
    const strength = 5_000_000 + tr * 10_000 + sum;
    return {
      category: 'TRIPLE',
      strength,
      value: tr,
      sum,
      type: 'Трынка',
      pointValue: sum,
      description: `Трынка ${RANK_NAMES_GEN[r] || r} (${sum} очков)`,
      legacyType: 'SEKA',
    };
  }

  // --- Группы по масти ---
  const suitGroups = {};
  cards.forEach((card) => {
    const suit = card[1];
    const v = getCardValue(card[0]);
    if (!suitGroups[suit]) suitGroups[suit] = [];
    suitGroups[suit].push(v);
  });

  let bestTroikaSum = -1;
  let bestTroikaSuit = null;
  let bestHlyustSum = -1;
  let bestHlyustSuit = null;
  let bestHlyustKicker = 0;

  for (const [suit, values] of Object.entries(suitGroups)) {
    if (values.length >= 3) {
      const sum = values.reduce((a, b) => a + b, 0);
      if (sum > bestTroikaSum) {
        bestTroikaSum = sum;
        bestTroikaSuit = suit;
      }
    } else if (values.length === 2) {
      const sum = values.reduce((a, b) => a + b, 0);
      const sorted = [...values].sort((a, b) => b - a);
      const kicker = sorted[0] * 10 + sorted[1];
      if (sum > bestHlyustSum || (sum === bestHlyustSum && kicker > bestHlyustKicker)) {
        bestHlyustSum = sum;
        bestHlyustSuit = suit;
        bestHlyustKicker = kicker;
      }
    }
  }

  if (bestTroikaSum >= 0 && bestTroikaSuit) {
    const strength = 3_000_000 + bestTroikaSum * 100 + 3;
    return {
      category: 'TROIKA',
      strength,
      value: bestTroikaSum,
      suit: bestTroikaSuit,
      type: 'Трояк',
      pointValue: bestTroikaSum,
      description: `Трояк ${SUIT_NAMES[bestTroikaSuit] || bestTroikaSuit} (${bestTroikaSum} очков)`,
      legacyType: 'TROIKA',
    };
  }

  if (bestHlyustSum >= 0 && bestHlyustSuit) {
    const strength = 2_000_000 + bestHlyustSum * 1_000 + bestHlyustKicker;
    return {
      category: 'HLYUST',
      strength,
      value: bestHlyustSum,
      suit: bestHlyustSuit,
      type: 'Хлюст',
      pointValue: bestHlyustSum,
      description: `Хлюст ${SUIT_NAMES[bestHlyustSuit] || bestHlyustSuit} (${bestHlyustSum} очков)`,
      legacyType: 'HLYUST',
    };
  }

  const vals = cards.map((c) => getCardValue(c[0])).sort((a, b) => b - a);
  const [v0, v1, v2] = vals;
  const strength = 1_000_000 + v0 * 10_000 + v1 * 100 + v2;
  return {
    category: 'SINGLE',
    strength,
    value: v0,
    type: 'Старшая карта',
    pointValue: v0,
    description: `Старшая карта (${v0} очков)`,
    legacyType: 'SINGLE',
  };
}

export function compareSekaHands(a, b) {
  return evaluateSekaHand(a).strength - evaluateSekaHand(b).strength;
}

/**
 * Локальная сессия раздачи. Вскрытие использует один общий банк; `sidePots` зарезервировано —
 * многосторонние side-pot при олл-инах в торговле пока не считаются (как на сервере).
 *
 * @param {object} config
 * @param {{ id: number, name: string }[]} [playerSeeds]
 */
export class LocalSekaGame {
  static calculatePotBreakdown(potAmount) {
    const potGross = Math.max(0, Math.floor(Number(potAmount) || 0));
    const rakeAmount = Math.floor(potGross * SEKA_RAKE_PERCENT);
    return {
      potGross,
      rakeAmount,
      potNet: potGross - rakeAmount,
    };
  }
  constructor(config = {}, playerSeeds = null) {
    this.config = { ...DEFAULT_SEKA_CONFIG, ...config };
    this.deck = [];
    this.players = playerSeeds
      ? playerSeeds.map((p, i) => ({
          id: p.id ?? i + 1,
          name: p.name ?? `Игрок ${i + 1}`,
          cards: [],
          chips: this.config.startingChips,
          currentBet: 0,
          isDark: true,
          isFolded: false,
          isSittingOut: false,
          pendingBuyIn: 0,
          /** Одноразово: не платить анте в следующей «жёсткой» свара-руке (локальный opt-out). */
          skipNextSvarkaAnte: false,
          /** В текущей раздаче вошёл неполным анте (all-in ante при Сваре ×N). */
          isAllInOnAnte: false,
          /** Олл-ин в торговле (стек 0, ждём вскрытия). */
          isAllIn: false,
        }))
      : [
          {
            id: 1,
            name: 'Игрок 1',
            cards: [],
            chips: this.config.startingChips,
            currentBet: 0,
            isDark: true,
            isFolded: false,
            isSittingOut: false,
            pendingBuyIn: 0,
            skipNextSvarkaAnte: false,
            isAllInOnAnte: false,
            isAllIn: false,
          },
          {
            id: 2,
            name: 'Игрок 2',
            cards: [],
            chips: this.config.startingChips,
            currentBet: 0,
            isDark: true,
            isFolded: false,
            isSittingOut: false,
            pendingBuyIn: 0,
            skipNextSvarkaAnte: false,
            isAllInOnAnte: false,
            isAllIn: false,
          },
        ];
    this.currentPlayer = 0;
    this.dealerIndex = 0;
    this.pot = 0;
    /** Верхняя (световая) ставка уровня стола — сколько нужно довести currentBet игрока */
    this.currentBet = 0;
    this.roundNumber = 1;
    this.gamePhase = 'preflop';
    this.gameEnded = false;
    this.winner = null;
    this.winners = [];
    this.gameHistory = [];
    this.lastAggressorIndex = -1;
    /** Зарезервировано под будущий side-pot; сейчас не заполняется. */
    this.sidePots = [];
    /** Кто уже сходил в текущем кругу торгов (для currentBet === 0) */
    this.playersActedThisRound = new Set();
    /** Последние списания анте по местам (для UI локальной игры). */
    this.lastAnteContributions = [];
    /** Множитель анте для следующей раздачи (свара после пустого круга). */
    this.svarkaAnteMultiplier = 1;
    /** Был ли рейз в текущей раздаче (для правила пустого круга). */
    this.handHadRaise = false;
    /** Подряд завершённых «пустых» свар (resolveEmptyCheckHand); при достижении лимита следующий пустой круг — шоудаун. */
    this.svarkaChainCount = 0;
    /** Лог действий текущей раздачи для экрана истории. */
    this.currentHandActionLog = [];
    /** Номер хода в пределах руки (паритет с серверным moveLog). */
    this._moveSeq = 0;
    /** Стеки игроков в начале текущей раздачи (до анте) для подсчёта +/- за руку. */
    this.currentHandStartStacks = null;
  }

  /** Эффективное анте за место с учётом свара (база × множитель, по потолку). */
  getEffectiveAnteAmount() {
    const base = Number(this.config.ante) || 0;
    const cap = Number(this.config.svarkaAnteMaxMultiplier) || 8;
    const m = Math.min(cap, Math.max(1, Number(this.svarkaAnteMultiplier) || 1));
    return base * m;
  }

  createDeck() {
    const suits = ['H', 'D', 'C', 'S'];
    const ranks = ['6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
    const deck = [];
    for (const suit of suits) {
      for (const rank of ranks) {
        deck.push(rank + suit);
      }
    }
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
  }

  /**
   * Может сделать ставку в текущем круге (есть фишки). Олл-ин с нулём на руках ход не получает.
   * Не путать с участием во вскрытии — там учитываются все не сбросившие.
   */
  canActInBettingRound(p) {
    return (
      Boolean(p) &&
      !p.isFolded &&
      !p.isSittingOut &&
      Number(p.chips || 0) > 0
    );
  }

  /** В розыгрыше (не пас и не sit-out) — паритет с server `isPlayerInHand`. */
  isPlayerInHand(p) {
    return Boolean(p) && !p.isFolded && !p.isSittingOut;
  }

  activePlayersInHand() {
    return this.players.filter((p) => this.isPlayerInHand(p));
  }

  /**
   * Первый, кто может торговать, по часовой стрелке после `from` (не включая from).
   * @returns {number} индекс места или **-1**, если никто не может сделать ставку (все в олл-ине).
   */
  firstActiveAfter(from) {
    const n = this.players.length;
    for (let step = 1; step <= n; step++) {
      const i = (from + step) % n;
      if (this.canActInBettingRound(this.players[i])) return i;
    }
    return -1;
  }

  /** Левее дилера — первый говорит после раздачи / нового круга торгов (-1 если некому). */
  firstSpeakerIndex() {
    return this.firstActiveAfter(this.dealerIndex);
  }

  /** Сколько не сбросивших ещё могут вложить фишки в этом круге. */
  countPlayersWhoCanActInBettingRound(activePlayers) {
    return activePlayers.filter((p) => this.canActInBettingRound(p)).length;
  }

  /**
   * Подготовка новой руки: докупки, колода, сброс полей, список с `chips > ante` (как раньше).
   * @param {{ svarkaCoalesceDepth?: number }} [opts] внутренний счётчик при слиянии «все отказались от свара».
   * @returns {{ canDealHole: boolean, eligiblePlayers: typeof this.players }}
   */
  prepareNewHand(opts = {}) {
    const svarkaCoalesceDepth = Math.max(0, Math.min(4, Number(opts.svarkaCoalesceDepth) || 0));
    this.handHadRaise = false;
    this.players.forEach((player) => {
      const queued = Number(player.pendingBuyIn || 0);
      if (queued > 0) {
        player.chips += queued;
        player.pendingBuyIn = 0;
      }
    });
    this.deck = this.createDeck();
    this.currentHandStartStacks = this.players.map((p) => ({
      id: p.id,
      name: p.name,
      chips: Number(p.chips || 0),
    }));
    this.currentHandActionLog = [];
    this._moveSeq = 0;
    const eligiblePlayers = this.players.filter(
      (player) => Number(player.chips || 0) > 0 && !player.skipNextSvarkaAnte
    );
    this.players.forEach((player) => {
      player.cards = [];
      player.currentBet = 0;
      player.isDark = true;
      player.isAllInOnAnte = false;
      player.isAllIn = false;
      const out = Number(player.chips || 0) <= 0 || player.skipNextSvarkaAnte;
      player.isSittingOut = out;
      player.isFolded = out;
    });
    this.lastAnteContributions = [];

    if (eligiblePlayers.length < 2) {
      const stacksWithChips = this.players.filter((p) => Number(p.chips || 0) > 0).length;
      /** Нельзя начать раздачу с текущим (дорогим) анте, но минимум двое ещё со стеком — отказ от свара, не конец матча. */
      if (stacksWithChips >= 2 && svarkaCoalesceDepth < 4) {
        this.players.forEach((p) => {
          p.skipNextSvarkaAnte = false;
        });
        this.svarkaAnteMultiplier = 1;
        this.svarkaChainCount = 0;
        this.players.forEach((p) => {
          const broke = Number(p.chips || 0) <= 0;
          p.isSittingOut = broke;
          p.isFolded = broke;
        });
        return this.prepareNewHand({ svarkaCoalesceDepth: svarkaCoalesceDepth + 1 });
      }

      const finalPot = Number(this.pot || 0);
      const { potGross, rakeAmount, potNet } = LocalSekaGame.calculatePotBreakdown(finalPot);
      this.players.forEach((p) => {
        p.skipNextSvarkaAnte = false;
      });
      if (eligiblePlayers.length === 1) {
        const carried = potNet;
        if (carried > 0) {
          eligiblePlayers[0].chips += carried;
        }
      }
      this.pot = 0;
      this.currentBet = 0;
      this.roundNumber = 1;
      this.gamePhase = 'showdown';
      this.gameEnded = true;
      this.winner = eligiblePlayers[0] ?? null;
      this.winners = eligiblePlayers[0] ? [eligiblePlayers[0]] : [];
      this.lastAggressorIndex = -1;
      this.sidePots = [];
      this.playersActedThisRound = new Set();
      this.currentPlayer = eligiblePlayers[0]
        ? this.players.findIndex((p) => p.id === eligiblePlayers[0].id)
        : 0;
      const handStartById = new Map(
        Array.isArray(this.currentHandStartStacks)
          ? this.currentHandStartStacks.map((s) => [s.id, Number(s.chips || 0)])
          : []
      );
      this.gameHistory.push({
        winner: this.winner,
        winners: this.winners,
        players: this.players.map((p) => ({
          id: p.id,
          name: p.name,
          chips: Number(p.chips || 0),
          isFolded: Boolean(p.isFolded),
          cards: [...(p.cards || [])],
        })),
        hands: [],
        stackDeltas: this.players.map((p) => {
          const start = handStartById.has(p.id) ? handStartById.get(p.id) : Number(p.chips || 0);
          return {
            playerId: p.id,
            playerName: p.name,
            startChips: start,
            endChips: Number(p.chips || 0),
            delta: Number(p.chips || 0) - start,
          };
        }),
        actions: [...this.currentHandActionLog],
        pot: potGross,
        potGross,
        rakeAmount,
        potNet,
        timestamp: new Date(),
      });
      return { canDealHole: false, eligiblePlayers: [] };
    }
    return { canDealHole: true, eligiblePlayers };
  }

  /**
   * Списать анте с участников (после {@link #prepareNewHand}).
   * @param {Array<typeof this.players[0]>} eligiblePlayers
   */
  collectAnteFromEligible(eligiblePlayers) {
    const effAnte = this.getEffectiveAnteAmount();
    let potTotal = 0;
    const contributions = [];
    eligiblePlayers.forEach((p) => {
      const take = Math.min(effAnte, Number(p.chips || 0));
      p.isAllInOnAnte = take > 0 && take < effAnte;
      p.chips -= take;
      potTotal += take;
      contributions.push({
        seatIndex: this.players.findIndex((pl) => pl.id === p.id),
        amount: take,
      });
    });
    this.pot = Number(this.pot || 0) + potTotal;
    this.lastAnteContributions = contributions;
  }

  /**
   * Раздать по 3 карты (анте уже собрано).
   * @param {Array<typeof this.players[0]>} eligiblePlayers
   */
  /**
   * @returns {boolean} true если раздача сразу завершена авто-вскрытием (all-in анте).
   */
  dealHoleCardsToEligible(eligiblePlayers) {
    this.players.forEach((p) => {
      p.skipNextSvarkaAnte = false;
    });
    for (let i = 0; i < 3; i++) {
      eligiblePlayers.forEach((player) => {
        player.cards.push(this.deck.pop());
      });
    }
    this.currentBet = 0;
    this.roundNumber = 1;
    this.gamePhase = 'preflop';
    this.gameEnded = false;
    this.winner = null;
    this.winners = [];
    this.lastAggressorIndex = -1;
    this.sidePots = [];
    this.playersActedThisRound = new Set();
    const nextSpeaker = this.firstSpeakerIndex();
    if (nextSpeaker < 0) {
      this.gamePhase = 'showdown';
      this.determineWinner();
      return true;
    }
    this.currentPlayer = nextSpeaker;
    this.handHadRaise = false;

    const anyShortAnte = eligiblePlayers.some((p) => Boolean(p.isAllInOnAnte));
    if (anyShortAnte) {
      const firstSeat = this.players.findIndex((pl) =>
        eligiblePlayers.some((e) => e.id === pl.id)
      );
      this.recordAction(firstSeat >= 0 ? firstSeat : 0, 'AUTO_SHOWDOWN_ALLIN_ANTE', {
        effAnte: this.getEffectiveAnteAmount(),
      });
      this.gamePhase = 'showdown';
      this.determineWinner();
      return true;
    }
    return false;
  }

  /**
   * Пустой первый круг: все чек без рейза — карты сбрасываются, банк остаётся, следующее анте с удвоением (свара).
   */
  resolveEmptyCheckHand() {
    const cap = Number(this.config.svarkaAnteMaxMultiplier) || 8;
    const cur = Math.max(1, Number(this.svarkaAnteMultiplier) || 1);
    this.svarkaAnteMultiplier = Math.min(cap, cur * 2);

    this.players.forEach((p) => {
      p.cards = [];
      p.currentBet = 0;
      p.isDark = true;
    });
    this.currentBet = 0;
    this.roundNumber = 1;
    this.playersActedThisRound = new Set();
    this.lastAggressorIndex = -1;
    this.handHadRaise = false;
    this.gameEnded = false;
    this.winner = null;
    this.winners = [];
    this.gamePhase = 'svarkaBetween';
    const svSpeaker = this.firstSpeakerIndex();
    this.currentPlayer = svSpeaker >= 0 ? svSpeaker : this.dealerIndex;
    this.svarkaChainCount += 1;
  }

  dealCards() {
    const prep = this.prepareNewHand();
    if (!prep.canDealHole) return;
    this.collectAnteFromEligible(prep.eligiblePlayers);
    this.dealHoleCardsToEligible(prep.eligiblePlayers);
  }

  /** Следующая раздача — кнопка дилера по кругу */
  rotateDealer() {
    this.dealerIndex = (this.dealerIndex + 1) % this.players.length;
  }

  evaluateHand(cards) {
    return evaluateSekaHand(cards);
  }

  markActed(playerIndex) {
    this.playersActedThisRound.add(this.players[playerIndex].id);
  }

  /**
   * Записать действие игрока в журнал текущей раздачи.
   * @param {number} playerIndex
   * @param {string} action
   * @param {object} details
   */
  recordAction(playerIndex, action, details = {}) {
    const p = this.players[playerIndex];
    if (!p) return;
    this._moveSeq += 1;
    const at = Date.now();
    this.currentHandActionLog.push({
      seatIndex: playerIndex,
      playerId: p.id,
      playerName: p.name,
      action: String(action || '').toUpperCase(),
      ...details,
      moveSeq: this._moveSeq,
      timestampMs: at,
      timestamp: new Date(at),
    });
  }

  /**
   * При коротком колле в HU второй игрок мог уже вложить больше фишек в «свет»;
   * возвращаем неувязанную часть из банка и выравниваем currentBet.
   */
  reconcileHeadsUpAfterShortCall() {
    const active = this.activePlayersInHand();
    if (active.length !== 2) return;

    const [a, b] = active;
    if (a.currentBet === b.currentBet) {
      this.currentBet = a.currentBet;
      return;
    }
    const hi = a.currentBet > b.currentBet ? a : b;
    const lo = a.currentBet > b.currentBet ? b : a;

    if (hi.currentBet > lo.currentBet && lo.chips === 0) {
      const refund = hi.currentBet - lo.currentBet;
      hi.chips += refund;
      this.pot -= refund;
      hi.currentBet = lo.currentBet;
    }
    this.currentBet = Math.max(active[0].currentBet, active[1].currentBet);
  }

  bettingRoundComplete(activePlayers) {
    if (activePlayers.length < 2) return false;

    if (this.currentBet === 0) {
      /**
       * Круг без ставки на столе: учитываем только тех, кто может ещё торговать.
       * Олл-ин с 0 фишек ход не получает — для них круг считается закрытым без markActed.
       */
      return activePlayers.every(
        (p) => !this.canActInBettingRound(p) || this.playersActedThisRound.has(p.id)
      );
    }

    const tableBet = Number(this.currentBet || 0);
    const allMatchedTop = activePlayers.every((p) => Number(p.currentBet || 0) >= tableBet);
    if (allMatchedTop) return true;

    if (activePlayers.some((p) => Number(p.chips || 0) > 0 && Number(p.currentBet || 0) < tableBet)) {
      return false;
    }

    /**
     * Side pots: вклады по местам могут различаться (100 / 100 / 50). Раунд закрыт, если у каждого активного
     * либо ставка на линии стола, либо олл-ин без остатка фишек.
     */
    const stakesResolvedForSidePots = activePlayers.every(
      (p) => Number(p.chips || 0) === 0 || Number(p.currentBet || 0) === tableBet
    );
    if (stakesResolvedForSidePots) return true;

    const maxContrib = Math.max(...activePlayers.map((p) => Number(p.currentBet || 0)));
    const minContrib = Math.min(...activePlayers.map((p) => Number(p.currentBet || 0)));
    /** Никто не может уравнять до линии: короткие стеки сведены (reconcile HU), ждём равенства взносов. */
    return maxContrib === minContrib && maxContrib === tableBet;
  }

  makeMove(playerIndex, action, amount = 0) {
    const player = this.players[playerIndex];
    const isDark = player.isDark;

    if (this.gameEnded || player.isFolded || playerIndex !== this.currentPlayer) {
      return false;
    }
    if (!this.canActInBettingRound(player)) {
      return false;
    }

    switch (action) {
      case 'CHECK':
        if (this.currentBet - player.currentBet > 0) {
          return false;
        }
        this.recordAction(playerIndex, 'CHECK');
        this.markActed(playerIndex);
        break;

      case 'LOOK':
        /** Смотреть карты — не завершает ход: затем обязательны чек/ставка/пас. */
        player.isDark = false;
        this.recordAction(playerIndex, 'LOOK');
        this.checkGameEnd();
        return true;

      case 'FOLD':
        player.isFolded = true;
        this.recordAction(playerIndex, 'FOLD');
        this.lastAggressorIndex = playerIndex;
        this.markActed(playerIndex);
        break;

      case 'CALL': {
        const neededToCall = this.currentBet - player.currentBet;
        if (neededToCall <= 0) {
          this.recordAction(playerIndex, 'CALL', { paid: 0, matched: 0 });
          this.markActed(playerIndex);
          break;
        }

        const chipsNeeded = isDark ? Math.floor(neededToCall / 2) : neededToCall;
        const pay = Math.min(player.chips, chipsNeeded);

        if (pay < chipsNeeded) {
          player.chips -= pay;
          this.pot += pay;
          const matched = isDark ? Math.min(neededToCall, pay * 2) : pay;
          player.currentBet += matched;
          if (player.chips === 0) player.isAllIn = true;
          this.recordAction(playerIndex, 'CALL', { paid: pay, matched, allIn: player.chips === 0 });
          if (this.players.length === 2) {
            this.reconcileHeadsUpAfterShortCall();
          }
        } else {
          player.chips -= pay;
          this.pot += pay;
          player.currentBet += neededToCall;
          if (player.chips === 0) player.isAllIn = true;
          this.recordAction(playerIndex, 'CALL', { paid: pay, matched: neededToCall, allIn: player.chips === 0 });
        }
        this.lastAggressorIndex = playerIndex;
        this.markActed(playerIndex);
        break;
      }

      case 'RAISE': {
        const minTarget = this.currentBet + this.config.minRaise;
        let targetBet = Math.max(amount || 0, minTarget);
        let neededToRaise = targetBet - player.currentBet;
        if (neededToRaise <= 0) return false;

        let chipsToPay = isDark ? Math.floor(neededToRaise / 2) : neededToRaise;
        if (chipsToPay > player.chips) {
          const pay = player.chips;
          const maxRaiseFromStack = isDark ? pay * 2 : pay;
          neededToRaise = Math.min(neededToRaise, maxRaiseFromStack);
          if (neededToRaise <= 0) return false;
          chipsToPay = isDark ? Math.floor(neededToRaise / 2) : neededToRaise;
          if (chipsToPay > pay) {
            chipsToPay = pay;
            neededToRaise = isDark ? pay * 2 : pay;
          }
          targetBet = player.currentBet + neededToRaise;
        }

        this.handHadRaise = true;
        player.chips -= chipsToPay;
        this.pot += chipsToPay;
        player.currentBet += neededToRaise;
        if (player.chips === 0) player.isAllIn = true;
        this.currentBet = player.currentBet;
        this.recordAction(playerIndex, 'RAISE', {
          paid: chipsToPay,
          targetBet: player.currentBet,
          matched: neededToRaise,
          allIn: player.chips === 0,
        });
        this.lastAggressorIndex = playerIndex;
        this.playersActedThisRound = new Set([player.id]);
        this.markActed(playerIndex);
        if (this.players.length === 2) {
          this.reconcileHeadsUpAfterShortCall();
        }
        break;
      }

      case 'SHOWDOWN':
        if (this.roundNumber >= this.config.showdownMinRound) {
          this.recordAction(playerIndex, 'SHOWDOWN');
          this.gamePhase = 'showdown';
          return this.determineWinner();
        }
        return false;

      default:
        return false;
    }

    this.nextPlayer();
    if (this.gameEnded) return true;
    return this.checkGameEnd();
  }

  nextPlayer() {
    if (this.gamePhase === 'showdown') return;

    const activePlayers = this.activePlayersInHand();
    if (activePlayers.length <= 1) {
      this.gamePhase = 'showdown';
      return;
    }

    if (this.bettingRoundComplete(activePlayers)) {
      if (
        this.roundNumber === 1 &&
        this.currentBet === 0 &&
        !this.handHadRaise &&
        activePlayers.length >= 2
      ) {
        const maxChain = Number(this.config.svarkaMaxChainBeforeShowdown) || 3;
        if (maxChain > 0 && this.svarkaChainCount >= maxChain) {
          this.gamePhase = 'showdown';
          this.determineWinner();
          return;
        }
        this.resolveEmptyCheckHand();
        return;
      }
      this.roundNumber++;
      this.lastAggressorIndex = -1;
      if (this.config.maxTradingRounds > 0 && this.roundNumber > this.config.maxTradingRounds) {
        this.gamePhase = 'showdown';
        this.determineWinner();
        return;
      }
      this.players.forEach((p) => {
        p.currentBet = 0;
      });
      this.currentBet = 0;
      this.playersActedThisRound = new Set();
      const nextSpeaker = this.firstSpeakerIndex();
      if (nextSpeaker < 0) {
        this.gamePhase = 'showdown';
        this.determineWinner();
        return;
      }
      this.currentPlayer = nextSpeaker;
      return;
    }

    const n = this.players.length;
    let nextIndex = this.currentPlayer;
    let guard = 0;
    do {
      nextIndex = (nextIndex + 1) % n;
      guard++;
      if (guard >= n) break;
    } while (!this.canActInBettingRound(this.players[nextIndex]));

    if (this.canActInBettingRound(this.players[nextIndex])) {
      this.currentPlayer = nextIndex;
      return;
    }

    const after = this.firstActiveAfter(this.currentPlayer);
    if (after < 0) {
      this.gamePhase = 'showdown';
      this.determineWinner();
      return;
    }
    this.currentPlayer = after;
  }

  checkGameEnd() {
    if (this.gameEnded) return true;
    const activePlayers = this.activePlayersInHand();
    if (activePlayers.length <= 1) {
      this.gamePhase = 'showdown';
      return this.determineWinner();
    }
    return false;
  }

  determineWinner() {
    if (this.gameEnded) return true;
    const activePlayers = this.activePlayersInHand();
    const potAmount = this.pot;
    const { potGross, rakeAmount, potNet } = LocalSekaGame.calculatePotBreakdown(potAmount);
    const handStartById = new Map(
      Array.isArray(this.currentHandStartStacks)
        ? this.currentHandStartStacks.map((s) => [s.id, Number(s.chips || 0)])
        : []
    );

    if (activePlayers.length === 0) {
      this.winner = null;
      this.winners = [];
    } else if (activePlayers.length === 1) {
      this.winner = activePlayers[0];
      this.winners = [activePlayers[0]];
      this.winner.chips += potNet;
    } else {
      const scored = activePlayers.map((player) => ({
        player,
        hand: evaluateSekaHand(player.cards),
      }));
      scored.sort((a, b) => b.hand.strength - a.hand.strength);
      const best = scored[0].hand.strength;
      const tops = scored.filter((s) => s.hand.strength === best);

      if (tops.length === 1) {
        this.winner = tops[0].player;
        this.winners = [tops[0].player];
        this.winner.chips += potNet;
      } else {
        this.winner = null;
        this.winners = tops.map((t) => t.player);
        const each = Math.floor(potNet / tops.length);
        tops.forEach((t) => {
          t.player.chips += each;
        });
        const remainder = potNet - each * tops.length;
        if (remainder > 0) {
          tops[0].player.chips += remainder;
        }
      }
    }

    this.pot = 0;
    this.gameEnded = true;
    this.svarkaAnteMultiplier = 1;
    this.svarkaChainCount = 0;

    const autoShowdownAllInAnte = activePlayers.some((p) => Boolean(p.isAllInOnAnte));

    this.gameHistory.push({
      winner: this.winner,
      winners: this.winners,
      autoShowdownAllInAnte,
      players: this.players.map((p) => ({
        id: p.id,
        name: p.name,
        chips: Number(p.chips || 0),
        isFolded: Boolean(p.isFolded),
        cards: [...(p.cards || [])],
      })),
      hands: activePlayers.map((p) => ({
        player: p,
        hand: evaluateSekaHand(p.cards),
      })),
      stackDeltas: this.players.map((p) => {
        const start = handStartById.has(p.id) ? handStartById.get(p.id) : Number(p.chips || 0);
        return {
          playerId: p.id,
          playerName: p.name,
          startChips: start,
          endChips: Number(p.chips || 0),
          delta: Number(p.chips || 0) - start,
        };
      }),
      actions: [...this.currentHandActionLog],
      pot: potGross,
      potGross,
      rakeAmount,
      potNet,
      timestamp: new Date(),
    });

    return true;
  }

  requestBuyIn(playerIndex, amount) {
    const player = this.players[playerIndex];
    if (!player) return { ok: false, code: 'SEAT_NOT_FOUND' };
    const buyInAmount = Math.floor(Number(amount) || 0);
    if (buyInAmount <= 0) return { ok: false, code: 'BAD_AMOUNT' };

    const activeParticipant = !this.gameEnded && this.isPlayerInHand(player);
    if (activeParticipant) {
      player.pendingBuyIn = Number(player.pendingBuyIn || 0) + buyInAmount;
      return { ok: true, mode: 'queued', amount: buyInAmount };
    }

    player.chips = Number(player.chips || 0) + buyInAmount;
    if (player.chips > 0) {
      player.isSittingOut = false;
      player.isFolded = false;
    }
    return { ok: true, mode: 'instant', amount: buyInAmount };
  }

  /**
   * Подготовка к новой раздаче без сброса стеков (непрерывная сессия).
   * Сдвигает баттон и заново раздаёт карты с анте (как dealCards).
   */
  newHand() {
    this.rotateDealer();
    this.dealCards();
  }

  /** Полный сброс партии (без смены имён игроков) */
  newGame() {
    this.deck = this.createDeck();
    this.players.forEach((player) => {
      player.cards = [];
      player.currentBet = 0;
      player.isDark = true;
      player.isFolded = false;
      player.isSittingOut = false;
      player.pendingBuyIn = 0;
      player.skipNextSvarkaAnte = false;
      player.isAllInOnAnte = false;
      player.isAllIn = false;
    });
    this.currentPlayer = 0;
    this.pot = 0;
    this.currentBet = 0;
    this.roundNumber = 1;
    this.gamePhase = 'preflop';
    this.gameEnded = false;
    this.winner = null;
    this.winners = [];
    this.lastAggressorIndex = -1;
    this.sidePots = [];
    this.playersActedThisRound = new Set();
    this.svarkaAnteMultiplier = 1;
    this.handHadRaise = false;
    this.svarkaChainCount = 0;
    this.currentHandActionLog = [];
    this._moveSeq = 0;
    this.currentHandStartStacks = null;
  }
}
